<template>
  <v-container fluid>
    <v-row class="flex-column">
      <v-col cols="12" class="px-0">
        <v-row class="d-flex">
          <v-col cols="12" class="font-size--16 mb-4">
            <p class="text-body-1">{{ $t('L_you_have_two_options') }}</p>
          </v-col>
          <v-col cols="12" class="font-size--16 mb-4">
            <p class="text-body-1">{{ $t('L_if_you_do_not_want_to_pay_all_past_due') }}</p>
          </v-col>
          <v-col cols="12" md="6" class="mb-4" :class="$vuetify.display.width > 820 ? 'pr-0' : ''">
            <div class="mb-4 font-weight-bold font-size--16">
              {{ $t('L_reinstate_with_original_issue_date') }}
            </div>
            <v-card
              @click="selectedAmount = 0"
              :class="selectedAmount == 0 ? 'cardSelected' : 'cardNotSelected'"
              class="clickable"
            >
              <v-row class="justify-end pa-4">
                <div class="cardAmount" v-if="get_paymentAsOfOrgIssueDate !== '$0.00'">
                  {{ get_paymentAsOfOrgIssueDate }}
                </div>
                <div v-if="selectedAmount === 0">
                  <v-icon color="green" :icon="mdiCheckCircleOutline" />
                </div>
                <v-card-text class="cardFooter text-left">
                  {{ $t('L_this_option_requires_you_to_pay_the_premium') }}
                </v-card-text>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <div class="mb-4 font-weight-bold font-size--16">
              {{ $t('L_reinstate_with_reissue_date') }}
            </div>
            <v-card
              @click="selectedAmount = 1"
              :class="selectedAmount === 1 ? 'cardSelected' : 'cardNotSelected'"
              class="clickable"
            >
              <v-row class="justify-end pa-4">
                <div class="cardAmount" v-if="get_paymentAsOfReIssueDate !== '$0.00'">
                  {{ get_paymentAsOfReIssueDate }}
                </div>
                <div v-if="selectedAmount === 1" class="text-right" style="width: 100%">
                  <v-icon color="green" :icon="mdiCheckCircleOutline" />
                </div>
                <v-card-text class="cardFooter text-left">
                  {{ $t('L_this_option_requires_a_redate_of_the_policy') }}
                </v-card-text>
              </v-row>
            </v-card>
          </v-col>
          <v-col
            cols="12"
            class="font-size--16 mt-4 mb-4"
            v-if="get_paymentAsOfOrgIssueDate !== '$0.00' && get_paymentAsOfReIssueDate !== '$0.00'"
          >
            <span v-html="$t('L_important_note_premium_amount_is_subject_to_change')" />
          </v-col>
        </v-row>

        <!-- Button -->
        <v-row class="d-flex align-center justify-center py-4">
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions"
              block
              variant="outlined"
              color="primary"
              @click="cancelAction"
            >
              {{ $t('L_cancel') }}
            </OnexButton>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions || selectedAmount === -1"
              target="_blank"
              block
              color="primary"
              @click="handleContinueAction"
            >
              {{ $t('L_continue') }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { currency } from '@/shared/util/formatters';
import { mapState } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { defineComponent, type ComponentOptions } from 'vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { mdiCheckCircleOutline } from '@mdi/js';
import { OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'ReinstatementProcessOptions',
  components: {
    OnexButton,
  },
  props: {
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selectedAmount: -1,
    mdiCheckCircleOutline,
  }),
  computed: {
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
    }),
    get_paymentAsOfOrgIssueDate() {
      return currency(+this.currentPolicy?.reinstatement?.paymentAsOfOrgIssueDate, 2);
    },
    get_paymentAsOfReIssueDate() {
      return currency(+this.currentPolicy?.reinstatement?.paymentAsOfReIssueDate, 2);
    },
  },
  methods: {
    handleContinueAction() {
      this.$emit('userOption', this.selectedAmount);
      this.continueAction();
    },
    continueAction() {
      this.$emit('continueClicked');
    },
    cancelAction() {
      this.$emit('cancelClicked');
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.bg-light-grey {
  background-color: #f5f5f5;
}

.card {
  max-width: 295px;
  padding: 15px;
}

.cardTitle {
  padding-top: 16px;
  padding-left: 16px;
  font-size: 12px;
  color: #333333;
}

.cardAmount {
  padding-top: 0;
  padding-left: 16px;
  font-size: 24px;
  font-weight: bold;
  color: #333333;
}

.cardFooter {
  padding-top: 8px;
  font-size: 12px;
  color: #333333;
}

.vertCenterCircleHoriRight {
  margin: auto;
  text-align: right;
  padding-right: 10px;
}

.firstTextContent,
.secondTextContent,
.thirdTextContent {
  font-size: 16px;
  color: #333333;
}

.firstTextContent {
  font-weight: 600;
}

.secondTextContent {
  font-weight: 400;
}

.thirdTextContent {
  font-weight: 600;
}

.maxwidthButton {
  max-width: 300px;
}

.moveRight {
  text-align: right;
}

.clickable {
  cursor: pointer;
  min-height: 70px;
}

.cardSelected {
  border: 2px solid #6cd07b;
  background-color: #d8f8dd;
}

.cardNotSelected {
  border: 2px solid #cccccc;
  background-color: #ffffff;
}
</style>
